import React, { useState, useEffect } from 'react';


const TransactionFlowVisualization = () => {
  const [activeNodes, setActiveNodes] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const nodes = [
    { id: 'btc', name: 'Bitcoin', x: 15, y: 30, color: '#F7931A', icon: 'Bitcoin' },
    { id: 'eth', name: 'Ethereum', x: 15, y: 70, color: '#627EEA', icon: 'Wallet' },
    { id: 'usdc', name: 'USDC', x: 40, y: 20, color: '#2775CA', icon: 'DollarSign' },
    { id: 'usdt', name: 'USDT', x: 40, y: 50, color: '#26A17B', icon: 'DollarSign' },
    { id: 'swap', name: 'Swap Engine', x: 50, y: 50, color: '#7C3AED', icon: 'RefreshCw' },
    { id: 'card', name: 'Card Rail', x: 40, y: 80, color: '#F59E0B', icon: 'CreditCard' },
    { id: 'merchant', name: 'Merchants', x: 85, y: 35, color: '#10B981', icon: 'Store' },
    { id: 'user', name: 'Users', x: 85, y: 65, color: '#2563EB', icon: 'Users' }
  ];

  const connections = [
    { from: 'btc', to: 'swap', volume: 245 },
    { from: 'eth', to: 'swap', volume: 189 },
    { from: 'usdc', to: 'swap', volume: 312 },
    { from: 'usdt', to: 'swap', volume: 278 },
    { from: 'card', to: 'swap', volume: 156 },
    { from: 'swap', to: 'merchant', volume: 534 },
    { from: 'swap', to: 'user', volume: 646 }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      const randomNode = nodes?.[Math.floor(Math.random() * nodes?.length)];
      setActiveNodes(prev => [...prev?.slice(-3), randomNode?.id]);

      const randomConnection = connections?.[Math.floor(Math.random() * connections?.length)];
      setTransactions(prev => [...prev?.slice(-5), {
        id: Date.now(),
        from: randomConnection?.from,
        to: randomConnection?.to,
        timestamp: new Date()
      }]);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const getNodeSize = (nodeId) => {
    const connection = connections?.find(c => c?.from === nodeId || c?.to === nodeId);
    const volume = connection ? connection?.volume : 100;
    return Math.max(40, Math.min(80, volume / 5));
  };

  const getConnectionWidth = (volume) => {
    return Math.max(2, Math.min(6, volume / 50));
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6 h-full">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h2 className="text-base md:text-lg font-semibold text-foreground">Live Transaction Flow</h2>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-xs md:text-sm text-muted-foreground caption">Live</span>
        </div>
      </div>
      <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] bg-background/50 rounded-lg overflow-hidden">
        <svg className="w-full h-full">
          <defs>
            {connections?.map((conn, idx) => (
              <linearGradient key={idx} id={`gradient-${idx}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={nodes?.find(n => n?.id === conn?.from)?.color} stopOpacity="0.6" />
                <stop offset="100%" stopColor={nodes?.find(n => n?.id === conn?.to)?.color} stopOpacity="0.6" />
              </linearGradient>
            ))}
          </defs>

          {connections?.map((conn, idx) => {
            const fromNode = nodes?.find(n => n?.id === conn?.from);
            const toNode = nodes?.find(n => n?.id === conn?.to);
            if (!fromNode || !toNode) return null;

            return (
              <line
                key={idx}
                x1={`${fromNode?.x}%`}
                y1={`${fromNode?.y}%`}
                x2={`${toNode?.x}%`}
                y2={`${toNode?.y}%`}
                stroke={`url(#gradient-${idx})`}
                strokeWidth={getConnectionWidth(conn?.volume)}
                strokeDasharray="5,5"
                className="transition-all duration-500"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="0"
                  to="10"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </line>
            );
          })}

          {nodes?.map((node) => {
            const size = getNodeSize(node?.id);
            const isActive = activeNodes?.includes(node?.id);

            return (
              <g key={node?.id}>
                <circle
                  cx={`${node?.x}%`}
                  cy={`${node?.y}%`}
                  r={size / 2}
                  fill={node?.color}
                  opacity={isActive ? 0.9 : 0.6}
                  className="transition-all duration-500"
                >
                  {isActive && (
                    <animate
                      attributeName="r"
                      from={size / 2}
                      to={size / 2 + 10}
                      dur="1s"
                      repeatCount="1"
                    />
                  )}
                </circle>
                <circle
                  cx={`${node?.x}%`}
                  cy={`${node?.y}%`}
                  r={size / 2 + 5}
                  fill="none"
                  stroke={node?.color}
                  strokeWidth="2"
                  opacity={isActive ? 0.5 : 0}
                  className="transition-all duration-500"
                />
              </g>
            );
          })}
        </svg>

        {nodes?.map((node) => (
          <div
            key={node?.id}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{ left: `${node?.x}%`, top: `${node?.y}%` }}
          >
            <div className="flex flex-col items-center gap-1">
              <div className="bg-card border border-border rounded-lg px-2 py-1 shadow-lg">
                <span className="text-xs font-medium text-foreground whitespace-nowrap">{node?.name}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        {nodes?.slice(0, 4)?.map((node) => (
          <div key={node?.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: node?.color }} />
            <span className="text-xs text-muted-foreground caption">{node?.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TransactionFlowVisualization;